import { formatMoney } from '@app/shared'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import type { RouterOutputs } from '../lib/trpc'
import { useTRPC } from '../lib/trpc'
import { Button, Notice, PaymentStep, useCheckoutConfirmation } from '../ui'
import { PayMembership } from './PayMembership'

type Membership = RouterOutputs['pay']['membership']

/**
 * What goes on happening on the account, below its statement and the way to
 * pay it: the membership, what the shul charges every household and this one
 * is charged, and the card automatic payments use. Each appears only where
 * the account has it. Where no membership section carries the card — no
 * membership standing, or none to offer — the card is added or replaced here.
 *
 * The statement already lists standing gifts, plans and pledges paid
 * separately, so they are not repeated here; this states only what it does
 * not.
 */
export function PayAccount({
  token,
  membership,
  orgShortId,
  who,
  onCardChange,
  onConfirmingChange,
}: {
  token: string
  membership: Membership | undefined
  orgShortId: string
  /** Whose account it is, as the statement names it. */
  who: string
  /** Told as the card is typed, so a kiosk counts it as activity. */
  onCardChange?: () => void
  /** Told while a card is being confirmed, so a kiosk holds its idle. */
  onConfirmingChange?: (confirming: boolean) => void
}) {
  const trpc = useTRPC()
  const qc = useQueryClient()
  const account = useQuery({
    ...trpc.pay.account.queryOptions({ token }),
    retry: false,
  })
  const card = useMutation({ ...trpc.pay.membershipCard.mutationOptions() })
  const [cardSaved, setCardSaved] = useState(false)
  const confirmation = useCheckoutConfirmation({
    onComplete: () => {
      card.reset()
      setCardSaved(true)
      void qc.invalidateQueries({ queryKey: trpc.pay.account.queryKey() })
      void qc.invalidateQueries({ queryKey: trpc.pay.autopay.queryKey() })
    },
    writeCheckoutToUrl: true,
  })
  const [joining, setJoining] = useState(false)
  /* Ending takes the membership off the record this section reads, so the
     section says what happened rather than quietly becoming an offer. */
  const [ended, setEnded] = useState(false)
  const a = account.data

  if (card.data) {
    return (
      <PaymentStep
        orgId={orgShortId}
        card={card.data.card}
        what="Automatic payments"
        who={who}
        amount="No charge"
        payLabel="Save card"
        confirmation={confirmation}
        onCardChange={onCardChange}
        onConfirmingChange={onConfirmingChange}
        onBack={() => card.reset()}
      />
    )
  }

  /* A standing membership, or one being started, holds the card in its own
     section; otherwise the card is offered on its own, so every account can
     keep one. */
  const membershipHoldsCard =
    membership !== undefined && (membership.arrangement !== null || joining)
  const cardholderId = a?.cardholderId ?? null
  const cardSection =
    !membershipHoldsCard && a && cardholderId ? (
      <section className="pay__autopay">
        <h2 className="pay__h2">Your card</h2>
        <p className="pay__auto-text">
          {a.autopayCard
            ? `${a.autopayCard} · The card automatic payments are taken from`
            : 'No card is on file. A card saved here is the one automatic payments are taken from.'}
        </p>
        {cardSaved && (
          <Notice>Your card is saved. It can take a moment to appear.</Notice>
        )}
        {card.error && <Notice tone="error">{card.error.message}</Notice>}
        <Button
          variant="secondary"
          disabled={card.isPending}
          onClick={() => card.mutate({ token, personId: cardholderId })}
        >
          {a.autopayCard ? 'Replace card' : 'Add card'}
        </Button>
      </section>
    ) : null

  /* A household with no membership is offered one quietly, behind one
     button, rather than a form in the middle of its statement. */
  const membershipSection = membership ? (
    membership.arrangement || joining ? (
      <PayMembership
        token={token}
        view={membership}
        besidePayment
        onCardChange={onCardChange}
        onConfirmingChange={onConfirmingChange}
        onEnded={() => {
          setEnded(true)
          setJoining(false)
        }}
      />
    ) : ended || membership.tiers.length > 0 ? (
      <section className="pay__autopay">
        <h2 className="pay__h2">Your membership</h2>
        {ended ? (
          <Notice>
            Your membership has ended. No later period will be billed, and
            anything already owed stays on your account.
          </Notice>
        ) : (
          <p className="pay__auto-text">
            No membership is standing on this account.
          </p>
        )}
        {membership.tiers.length > 0 && (
          <Button variant="secondary" onClick={() => setJoining(true)}>
            Start a membership
          </Button>
        )}
      </section>
    ) : null
  ) : null

  return (
    <>
      {membershipSection}
      {cardSection}
      {a &&
      (a.recurring.length > 0 || (membershipHoldsCard && a.autopayCard)) ? (
        <section className="pay__autopay">
          <h2 className="pay__h2">Also on your account</h2>
          {a.recurring.map((r) => (
            <div className="pay__auto" key={r.id}>
              <p className="pay__auto-head">
                {r.label} · {formatMoney(r.amountCents)}
              </p>
              <p className="pay__auto-text">
                Charged to your account when it comes due
              </p>
            </div>
          ))}
          {membershipHoldsCard && a.autopayCard && (
            <div className="pay__auto">
              <p className="pay__auto-head">{a.autopayCard}</p>
              <p className="pay__auto-text">
                The card automatic payments are taken from
              </p>
            </div>
          )}
        </section>
      ) : null}
    </>
  )
}
